import {useQueryClient, useMutation, useQuery, useInfiniteQuery} from '@tanstack/react-query';
import {AxiosResponse} from 'axios';
import {
  fetchEvent,
  updateEventAttendance,
  fetchEventRSVPStats,
  updateEventNotificationPreference,
  fetchPublicEvent,
  fetchEventInterestedGoingUsers,
  fetchEventBookmarkedUsers,
} from '../endpoints/api';
import {useDispatch} from 'react-redux';
import {OPTIONS} from '../constants/eventOptions';
import {RSVPModalPopupActions} from '../state/RSVPModalPopup/RSVPModalPopupSlice';
import useAuth from './useAuth';
import {EventNotificationsModalActions} from '../state/EventNotificationsModal/EventNotificationsModalSlice';
import {logEventRSVP} from '../utils/analytics';
import {ToastMessage} from '../components/toast';

export enum EventRSVPStatus {
  NO_STATUS = 0,
  INTERESTED = 1,
  GOING = 2,
  NOT_GOING = 3,
  NOT_INTERESTED = 4,
}

export function useFetchEvent(eventId: number | undefined, enabled = true) {
  return useQuery(['event', eventId], () => fetchEvent({id: Number(eventId)}), {
    enabled: enabled && !!eventId && !isNaN(eventId) && typeof eventId === 'number',
  });
}

export function useFetchPublicEvent(eventId: number | undefined) {
  return useQuery(['public_event', eventId], () => fetchPublicEvent({id: Number(eventId)}), {
    enabled: !!eventId && typeof eventId === 'number',
  });
}

export function useSetAttendance() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const {user} = useAuth();

  return useMutation(
    ({
      eventId,
      status,
      showDispatchRSVPModalPopupActions = true,
      showToast = true,
    }: {
      eventId: number;
      prevStatus: number;
      status: number;
      showDispatchRSVPModalPopupActions?: boolean;
      showToast?: boolean;
    }) => updateEventAttendance(eventId, status),
    {
      // This will at least optimitisticly update on the event page,
      // but the event cards on feeds would still need to wait for onSettled to be udpated
      onMutate: async (newStatus) => {
        // Cancel any outgoing refetches
        // (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({queryKey: ['event', newStatus.eventId]});
        await queryClient.cancelQueries({queryKey: ['meetup', newStatus.eventId]});
        await queryClient.cancelQueries({queryKey: ['eventInterestedGoingUsers', newStatus.eventId]});
        // Snapshot the previous value
        const previousEventStatus: AxiosResponse | null | undefined = queryClient.getQueryData([
          'event',
          newStatus.eventId,
        ]);

        const previousMeetupStatus: AxiosResponse | null | undefined = queryClient.getQueryData([
          'meetup',
          newStatus.eventId,
        ]);

        if (previousEventStatus) {
          // Optimistically update to the new value
          const isBookmarkingStatus = newStatus.status === EventRSVPStatus.INTERESTED || newStatus.status === EventRSVPStatus.GOING;
          queryClient.setQueryData(['event', newStatus.eventId], {
            ...previousEventStatus,
            data: {
              ...previousEventStatus.data,
              rsvp_status: {
                status: EventRSVPStatus[newStatus.status],
                status_value: newStatus.status,
              },
              // Backend auto-bookmarks on Going/Interested
              ...(isBookmarkingStatus && {is_bookmarked: true}),
            },
          });
        }

        if (previousMeetupStatus) {
          const isBookmarkingStatus = newStatus.status === EventRSVPStatus.INTERESTED || newStatus.status === EventRSVPStatus.GOING;
          queryClient.setQueryData(['meetup', newStatus.eventId], {
            ...previousEventStatus,
            data: {
              ...previousMeetupStatus.data,
              rsvp_status: {
                status: EventRSVPStatus[newStatus.status],
                status_value: newStatus.status,
              },
              // Backend auto-bookmarks on Going/Interested
              ...(isBookmarkingStatus && {is_bookmarked: true}),
            },
          });
        }

        // Optimistically add current user to interested/going list if RSVP'ing as INTERESTED or GOING.
        // Skip if user already appears anywhere in the paginated cache — otherwise the AvatarGroup
        // ends up with duplicate React keys (e.g. when auto-RSVP fires for a list that already
        // contains the current user from a previous fetch).
        if (newStatus.status === EventRSVPStatus.INTERESTED || newStatus.status === EventRSVPStatus.GOING) {
          const previousInterestedUsers: any = queryClient.getQueryData(['eventInterestedGoingUsers', newStatus.eventId]);
          if (previousInterestedUsers && user) {
            const alreadyListed = previousInterestedUsers.pages.some((page: any) =>
              (page?.data?.data || []).some((u: any) => u?.user_id === user.id),
            );
            if (!alreadyListed) {
              queryClient.setQueryData(['eventInterestedGoingUsers', newStatus.eventId], {
                ...previousInterestedUsers,
                pages: previousInterestedUsers.pages.map((page, index) => {
                  if (index === 0) {
                    // Add user to first page
                    return {
                      ...page,
                      data: {
                        ...page.data,
                        total: page.data.total + 1,
                        data: [
                          {
                            user_id: user.id,
                            name: user.name,
                            handle: user.handle,
                            profile_photo_url: user.profilePhotoUrl,
                            rsvp_status: EventRSVPStatus[newStatus.status],
                            created_at: new Date().toISOString(),
                          },
                          ...page.data.data,
                        ],
                      },
                    };
                  }
                  return {
                    ...page,
                    data: {
                      ...page.data,
                      total: page.data.total + 1,
                    },
                  };
                }),
              });
            }
          }
        } else if (newStatus.status === EventRSVPStatus.NOT_INTERESTED || newStatus.status === EventRSVPStatus.NOT_GOING) {
          // Optimistically remove current user from interested/going list if changing to NOT_INTERESTED or NOT_GOING
          const previousInterestedUsers: any = queryClient.getQueryData(['eventInterestedGoingUsers', newStatus.eventId]);
          if (previousInterestedUsers && user) {
            queryClient.setQueryData(['eventInterestedGoingUsers', newStatus.eventId], {
              ...previousInterestedUsers,
              pages: previousInterestedUsers.pages.map((page) => ({
                ...page,
                data: {
                  ...page.data,
                  total: Math.max(0, page.data.total - 1),
                  data: page.data.data.filter((u) => u.user_id !== user.id),
                },
              })),
            });
          }
        }

        // Return a context with the previous and new status
        return {previousStatus: previousEventStatus, newStatus};
      },
      onSettled(
        response,
        error,
        {eventId, prevStatus, status, showDispatchRSVPModalPopupActions = true, showToast = true},
        context
      ) {
        if(error) {
          ToastMessage({
            status: 'error',
            showStatusIcon: true,
            text: (error instanceof Error ? error.message : 'Something went wrong. Please try again later.'),
          });
        } else {
          const shouldShowRSVPModal = (storageKey) => {
            const doNotShowAgainRSVPModalObj = JSON.parse(localStorage.getItem(storageKey) || '[]');
            const index = doNotShowAgainRSVPModalObj.findIndex((obj) => obj.statusId === status);
            if (index !== -1 && doNotShowAgainRSVPModalObj[index].count >= 3) {
              showDispatchRSVPModalPopupActions = false;
            }
          };
          shouldShowRSVPModal('do-not-show-again-rsvp-modal');

          // Track Google Analytics event for a successful RSVP
          if (response?.data?.status && !error) {
            logEventRSVP(eventId, status);
          }

          // Auto-bookmark is now handled by the backend when RSVP is Going/Interested

          if (
            prevStatus !== status &&
            showDispatchRSVPModalPopupActions &&
            (((prevStatus === 0 || prevStatus === EventRSVPStatus.NOT_GOING) &&
                (status === EventRSVPStatus.INTERESTED || status === EventRSVPStatus.GOING)) ||
              status === EventRSVPStatus.NOT_GOING ||
              (prevStatus !== 0 && status === EventRSVPStatus.NOT_INTERESTED)) //CT-2507 Do not show “I’m not interested” popup if the user is selecting this status from default
          ) {
            dispatch(
              RSVPModalPopupActions.pushRSVPStatus({
                eventId,
                prevStatus,
                status,
                ...OPTIONS[status].popup,
              })
            );
          } else {
            const showEventModal =
              !!!(user?.emailEnabled && user.pushEnabled && user.smsEnabled) && user?.hasEarlyAccess;
            if (showEventModal)
              dispatch(EventNotificationsModalActions.pushEventNotifications({eventId}));
          }

          if (
            (showToast &&
              prevStatus === EventRSVPStatus.NOT_INTERESTED &&
              (status === EventRSVPStatus.GOING || status === EventRSVPStatus.INTERESTED)) ||
            (prevStatus === EventRSVPStatus.INTERESTED && status === EventRSVPStatus.GOING) ||
            (prevStatus === EventRSVPStatus.GOING && status === EventRSVPStatus.INTERESTED)
          ) {
            OPTIONS[status].message(
              'Thanks! Your RSVP is updated, and the event is added to your Bookmarks.'
            );
          }
        }

        queryClient
          .getQueryCache()
          .getAll()
          .filter(
            (query) =>
              (query.queryKey.includes('offers') &&
                (query.queryKey as any)[1].articleType === 'eventId' &&
                (query.queryKey as any)[1].articleId === `${eventId}`) ||
              query.queryKey.includes('infinite-global-search')
          )
          .forEach((query) => {
            return queryClient.invalidateQueries(query.queryKey);
          });
        queryClient.invalidateQueries([
          'infinite-offers',
          {articleType: 'eventId', articleId: eventId},
          'events',
          'event',
          eventId,
        ]);
        queryClient.invalidateQueries(['events']);
        queryClient.invalidateQueries(['event', eventId]);
        queryClient.invalidateQueries(['infinite-events']);
        queryClient.invalidateQueries(['RSVPStats', eventId]);
        queryClient.invalidateQueries(['eventInterestedGoingUsers', eventId]);
        queryClient.invalidateQueries(['current-home-context-content']);
        queryClient.invalidateQueries(['infinite-bookmarks']);
        queryClient.invalidateQueries(['place']);
        queryClient.invalidateQueries(['infinite-events-rsvps']);
        queryClient.invalidateQueries(['infinite_meetups']);
        queryClient.invalidateQueries(['infinite-global-search']);
      },
      // onSuccess: (response, {eventId, status}) => {
      //   queryClient
      //     .getQueryCache()
      //     .getAll()
      //     .filter(
      //       (query) =>
      //         (query.queryKey.includes('offers') &&
      //           (query.queryKey as any)[1].articleType === 'eventId' &&
      //           (query.queryKey as any)[1].articleId === `${eventId}`) ||
      //         query.queryKey.includes('infinite-global-search')
      //     )
      //     .forEach((query) => {
      //       return queryClient.invalidateQueries(query.queryKey);
      //     });
      //   queryClient.invalidateQueries([
      //     'infinite-offers',
      //     {articleType: 'eventId', articleId: eventId},
      //     'events',
      //     'event',
      //     eventId,
      //   ]);
      //   queryClient.invalidateQueries(['events']);
      //   queryClient.invalidateQueries(['event', eventId]);
      //   queryClient.invalidateQueries(['infinite-events']);
      //   queryClient.invalidateQueries(['RSVPStats', eventId]);
      //   queryClient.invalidateQueries(['current-home-context-content']);
      //   queryClient.invalidateQueries(['infinite-bookmarks']);
      //   queryClient.invalidateQueries(['place']);
      // },
    }
  );
}

export function useFetchEventRSVPStats(eventId: number, enabled?: boolean) {
  return useQuery(['RSVPStats', eventId], () => fetchEventRSVPStats(eventId), {enabled: enabled});
}

export function useFetchEventInterestedGoingUsers(eventId: number) {
  return useInfiniteQuery(
    ['eventInterestedGoingUsers', eventId],
    ({pageParam = 1}) => fetchEventInterestedGoingUsers(eventId, pageParam),
    {
      enabled: !!eventId,
      getNextPageParam: (lastPage) => {
        const {current_page, last_page} = lastPage.data;
        return current_page < last_page ? current_page + 1 : undefined;
      },
    }
  );
}

export function useFetchEventBookmarkedUsers(eventId: number, enabled: boolean = true) {
  return useInfiniteQuery(
    ['eventBookmarkedUsers', eventId],
    ({pageParam = 1}) => fetchEventBookmarkedUsers(eventId, pageParam),
    {
      enabled: enabled && !!eventId,
      getNextPageParam: (lastPage) => {
        const {current_page, last_page} = lastPage.data;
        return current_page < last_page ? current_page + 1 : undefined;
      },
    }
  );
}

export function useUpdateNotificationPreference() {
  return useMutation(
    ({eventId, receiveNotifications}: {eventId: number; receiveNotifications: boolean}) =>
      updateEventNotificationPreference(eventId, receiveNotifications)
  );
}
